import { inject } from '@angular/core';
import type { ActivatedRouteSnapshot, CanActivateFn, RouterStateSnapshot, UrlTree } from '@angular/router';
import { NavigationService } from '@core/navigation/services/navigation.service';
import { AppPathsMap } from '@core/routing/app-paths';
import { ImpersonationService } from '@core/services/impersonation.service';
import { ReturnUrlParameter, sanitizeReturnUrl } from '@core/utils/return-url.utilities';
import { LoggerService } from '@kerberos-compliance/compliance-shared-ui';
import { AuthFacade } from '@/auth/facades/auth.facade';

/**
 * Route guard for every authenticated area of the application.
 *
 * Hanko is the only identity provider, so there is no external redirect to
 * negotiate: a visitor either has a session (an impersonation context, or a
 * Hanko-established session backed by the ADP platform token) or is sent to the
 * login page. Returning a `UrlTree` keeps the redirect part of the same
 * navigation, so no intermediate route is activated.
 *
 * The blocked URL travels along as the `redirect` query parameter so the login
 * completion can resume it instead of dropping the visitor on the home page.
 *
 * @param {ActivatedRouteSnapshot} _route - The route being activated (unused; the full URL is taken from the state).
 * @param {RouterStateSnapshot} state - Router state, used to capture the requested URL.
 * @returns {boolean | UrlTree} `true` when the route may activate, otherwise a redirect to the login page.
 */
export const sessionGuard: CanActivateFn = (
  _route: ActivatedRouteSnapshot,
  state: RouterStateSnapshot,
): boolean | UrlTree => {
  const impersonationService = inject(ImpersonationService);
  const authFacade = inject(AuthFacade);
  const navigationService = inject(NavigationService);
  const logger = inject(LoggerService);

  if (impersonationService.shouldBypassExternalAuth()) {
    logger.debug('[sessionGuard] Impersonation context active, allowing activation.');
    return true;
  }

  // Tolerant on purpose: a login mid-exchange (provider marker set, platform token
  // not yet stored) must not be mistaken for an anonymous visitor and bounced off
  // the route it is about to land on.
  if (authFacade.hasSessionInFlightOrEstablished()) {
    return true;
  }

  const returnUrl = sanitizeReturnUrl(state.url);
  logger.debug('[sessionGuard] No session, redirecting to the login page.');
  return navigationService.createUrlTree(
    [AppPathsMap.Login],
    returnUrl ? { queryParams: { [ReturnUrlParameter]: returnUrl } } : undefined,
  );
};
